import { FieldNames } from 'Src/constants/FieldNames'
import { OrderModel, fieldsOrders, ReportType } from 'Pages/orders_design'
import { WrappedFormUtils } from 'antd/lib/form/Form'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { ProductSlugs } from 'Constants/ProductSlugs'
import { urls } from 'Constants/Urls'
import { union } from 'lodash'
import { RequestParams, ShortDataModel } from 'Models'

export class CreateOrderCore {
	public fields: FieldNames[] = []
	public requiredFields: FieldNames[] = []

	public constructor(fields: FieldNames[] = [], requiredFields: FieldNames[] = []) {
		this.fields = this.sortFields(fields, fieldsOrders.allFields)
		this.requiredFields = requiredFields
	}

	private sortFields = (fields: FieldNames[] = [], fieldsOrder: FieldNames[]): FieldNames[] => {
		const resFields: FieldNames[] = []
		fieldsOrder.forEach((orderedField: FieldNames): void =>
			fields.forEach((fieldForSort): void => {
				if (orderedField === fieldForSort) {
					resFields.push(orderedField)
				}
			})
		)
		return resFields
	}

	public hasFieldInRequired = (field: FieldNames): boolean =>
		!!this.requiredFields.find((f: FieldNames): boolean => f === field)

	public getFullName = (form: WrappedFormUtils): string | undefined => {
		const LAST_NAME = form.getFieldValue(FieldNames.LAST_NAME) ?? ''
		const FIRST_NAME = form.getFieldValue(FieldNames.FIRST_NAME) ?? ''
		const MIDDLE_NAME = form.getFieldValue(FieldNames.MIDDLE_NAME) ?? ''

		let fullName = `${LAST_NAME} ${FIRST_NAME} ${MIDDLE_NAME}`

		if (!LAST_NAME.length && !FIRST_NAME.length && !MIDDLE_NAME.length) {
			fullName = undefined
		}
		return fullName
	}

	public setFullName = (form: WrappedFormUtils, fieldValue: string, fieldType: string): void => {
		const LAST_NAME = form.getFieldValue(FieldNames.LAST_NAME) ?? ''
		const FIRST_NAME = form.getFieldValue(FieldNames.FIRST_NAME) ?? ''
		const MIDDLE_NAME = form.getFieldValue(FieldNames.MIDDLE_NAME) ?? ''

		const configValues = {
			[FieldNames.LAST_NAME]: `${fieldValue} ${FIRST_NAME} ${MIDDLE_NAME}`,
			[FieldNames.FIRST_NAME]: `${LAST_NAME} ${fieldValue} ${MIDDLE_NAME}`,
			[FieldNames.MIDDLE_NAME]: `${LAST_NAME} ${FIRST_NAME} ${fieldValue}`,
		}

		if (form.getFieldValue(FieldNames.UI_DECISION_MAKER_PERSON_NAME)) {
			form.setFieldsValue({
				[FieldNames.DECISION_MAKER_PERSON_NAME]: configValues[fieldType].length
					? configValues[fieldType]
					: undefined,
				[`decision_maker_person_${fieldType}`]: fieldValue?.length ? fieldValue : undefined,
			})
		}

		if (form.getFieldValue(FieldNames.UI_FINANCE_PERSON_NAME)) {
			form.setFieldsValue({
				[FieldNames.FINANCE_PERSON_NAME]: configValues[fieldType].length
					? configValues[fieldType]
					: undefined,
			})
		}

		if (form.getFieldValue(FieldNames.UI_MANAGER_NAME)) {
			form.setFieldsValue({
				[FieldNames.MANAGER_NAME]: configValues[fieldType].length
					? configValues[fieldType]
					: undefined,
				[`manager_${fieldType}`]: fieldValue?.length ? fieldValue : undefined,
			})
		}
	}
	public setName = (
		fieldType: string,
		form: WrappedFormUtils,
		event?: CheckboxChangeEvent | boolean
	): void => {
		const stateCheckbox = typeof event === 'boolean' ? event : event?.target.checked
		const { last_name, first_name, middle_name } = form.getFieldsValue()
		form.setFieldsValue({
			[`${fieldType}_name`]: stateCheckbox ? this.getFullName(form) : undefined,
			[`${fieldType}_last_name`]: stateCheckbox ? last_name : undefined,
			[`${fieldType}_first_name`]: stateCheckbox ? first_name : undefined,
			[`${fieldType}_middle_name`]: stateCheckbox ? middle_name : undefined,
		})
	}
	public bindSetDefaultValue =
		(
			form: WrappedFormUtils
		): ((
			field: FieldNames,
			defaultFieldName?: FieldNames,
			checkbox?: FieldNames | boolean
		) => void) =>
		(field: FieldNames, defaultFieldName?: FieldNames, checkbox?: FieldNames | boolean): void => {
			const { getFieldValue, setFieldsValue } = form
			const defaultValue =
				field === FieldNames.FINANCE_PERSON_NAME
					? this.getFullName(form)
					: getFieldValue(defaultFieldName)
			const checked = typeof checkbox === 'boolean' ? checkbox : !getFieldValue(checkbox)
			const initValue = field.includes('phone') ? '' : undefined
			setFieldsValue({
				[field]: checked ? defaultValue : initValue,
			})
		}

	public UI_FIELDS = [
		FieldNames.UI_DECISION_MAKER_PERSON_NAME,
		FieldNames.UI_DECISION_MAKER_PHONE,
		FieldNames.UI_DECISION_MAKER_EMAIL,
		FieldNames.UI_FINANCE_PERSON_NAME,
		FieldNames.UI_FINANCE_PERSON_EMAIL,
		FieldNames.UI_MANAGER_NAME,
		FieldNames.UI_MANAGER_EMAIL,
		FieldNames.UI_MANAGER_PHONE,
	]

	public setUIFields = (form: WrappedFormUtils, state: boolean): void => {
		form.setFieldsValue(
			this.UI_FIELDS.reduce((acc, item): any => {
				acc[item] = state
				return acc
			}, {})
		)
	}
}

const getValueOther = (order: OrderModel): { [key in keyof FieldNames]?: any } => {
	const onlineProductIds = {
		[FieldNames.ONLINE_PRODUCT_IDS]: union(
			order?.online_product_ids,
			order?.online_manager_product_ids
		),
		[FieldNames.ONLINE_MANAGER_PRODUCT_IDS]: [],
	}
	return {
		...order,
		...onlineProductIds,
	}
}

export const prepareOrderToSave = (order: OrderModel): OrderModel => {
	const values = getValueOther(order)
	// Адреса сериализовать
	if (values[FieldNames.POSADDRESS]) {
		values[FieldNames.POSADDRESS_KLADR_ID] = values[FieldNames.POSADDRESS].kladr_id
		values[FieldNames.POSADDRESS_POSTAL_CODE] = values[FieldNames.POSADDRESS].postal_code
		values[FieldNames.POSADDRESS_STREET_TYPE_FULL] = values[FieldNames.POSADDRESS].street_type_full
		values[FieldNames.POSADDRESS_STREET] = values[FieldNames.POSADDRESS].street
		values[FieldNames.POSADDRESS_HOUSE_TYPE_FULL] = values[FieldNames.POSADDRESS].house_type_full
		values[FieldNames.POSADDRESS_HOUSE] = values[FieldNames.POSADDRESS].house
		values[FieldNames.POSADDRESS_FLAT] = values[FieldNames.POSADDRESS].flat
		// @ts-ignore
		values[FieldNames.POSADDRESS_FIAS_GUID] = values[FieldNames.POSADDRESS].fias_id
		// @ts-ignore
		values[FieldNames.POSADDRESS_FIAS_LVL] = values[FieldNames.POSADDRESS].fias_level
		// @ts-ignore
		values[FieldNames.POSADDRESS] = values[FieldNames.POSADDRESS].result
	}
	if (values[FieldNames.ORGADDRESS]) {
		values[FieldNames.ORGADDRESS_KLADR_ID] = values[FieldNames.ORGADDRESS].kladr_id
		values[FieldNames.ORGADDRESS_POSTAL_CODE] = values[FieldNames.ORGADDRESS].postal_code
		values[FieldNames.ORGADDRESS_STREET_TYPE_FULL] = values[FieldNames.ORGADDRESS].street_type_full
		values[FieldNames.ORGADDRESS_STREET] = values[FieldNames.ORGADDRESS].street
		values[FieldNames.ORGADDRESS_HOUSE_TYPE_FULL] = values[FieldNames.ORGADDRESS].house_type_full
		values[FieldNames.ORGADDRESS_HOUSE] = values[FieldNames.ORGADDRESS].house
		values[FieldNames.ORGADDRESS_FLAT] = values[FieldNames.ORGADDRESS].flat
		// @ts-ignore
		values[FieldNames.ORGADDRESS_FIAS_GUID] = values[FieldNames.ORGADDRESS].fias_id
		// @ts-ignore
		values[FieldNames.ORGADDRESS_FIAS_LVL] = values[FieldNames.ORGADDRESS].fias_level
		// @ts-ignore
		values[FieldNames.ORGADDRESS] = values[FieldNames.ORGADDRESS].result
	}

	if (values[FieldNames.INSTALL_DATE]) {
		// @ts-ignore
		values[FieldNames.INSTALL_DATE] = values[FieldNames.INSTALL_DATE].toString()
	}

	if (!values[FieldNames.IS_CLIENT]) {
		values[FieldNames.ACCOUNT_BANK_BIC] = undefined
		values[FieldNames.ACCOUNT_NUMBER] = undefined
	}

	values[FieldNames.IS_CLIENT] = undefined

	if (values[FieldNames.ATTACHMENTS_ATTRIBUTES]?.length) {
		values[FieldNames.ATTACHMENTS_ATTRIBUTES] = (
			values[FieldNames.ATTACHMENTS_ATTRIBUTES] as File[]
		).map((file): { file: File } => {
			return { file }
		})
	}

	return Object.assign(new OrderModel(), order, values)
}

export const isDisabledButtonTimer = (timer): boolean => (timer ?? 1) > 0

export const getUrlForResendEmail = (productSlug: ProductSlugs): string => {
	switch (productSlug) {
		case ProductSlugs.PREMIUM_DS:
			return urls.orders.resend_premium_email
		default:
			return null
	}
}

export const getUrlForResendSms = (productSlug: ProductSlugs): string => {
	switch (productSlug) {
		case ProductSlugs.NAVIGATOR_MSP:
			return urls.orders.resend_msp_sms
		case ProductSlugs.PREMIUM_DS:
			return urls.orders.resend_premium_sms
		default:
			return null
	}
}

export const getUrlOrdersReport = (type: ReportType): string => {
	switch (type) {
		case ReportType.SMALL:
			return urls.orders.small_reports
		case ReportType.EXTENDED:
			return urls.orders.reports
		case ReportType.UNBLOCKREQUESTS:
			return urls.orders.unblock_requests_reports
		case ReportType.DZO_ORDERS:
			return urls.orders.dzo_reports
		default:
			return null
	}
}

export const arrayToShortDataArray = (array: string[] = []): ShortDataModel[] =>
	array.map((item) => ({
		id: item,
		name: item,
	}))

// todo fix after typing RequestParams
export function checkIsCompleteParams<T>(params: RequestParams, requiredFields: Array<keyof T>) {
	return !requiredFields.some((key) => typeof params[key as keyof RequestParams] === 'undefined')
}
